import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { DateRange } from "react-day-picker";
import { formatISO, isValid } from "date-fns";
import { fromJS } from "immutable";
import { ChevronDown, EllipsisVertical, Plus, Search } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../../../../components/shadcn/popover";
import { renderFieldIcon } from "../../../../fields/FieldList";
import { FilterRuleProps } from "../../../../main/component/filter/AdvancedFilter";
import { colorCodes } from "../../../../board/components/TaskDetail/ColorPicker";
import { DateRangePickerField } from "../../../../components/ui";
import InputDebounce from "../../../../components/InputDebounce";
import { search as apiSearch } from "../../../../api/ProjectApi";
import FieldPicker from "./FieldPicker";

/* ─── Extracted select value picker (needs its own useState for search) ─── */
type SelectValuePickerProps = {
  options: any[];
  selected: Set<string>;
  onChange: (newValues: (string | number)[]) => void;
  rule: FilterRuleProps;
};

const SelectValuePicker = ({ options, selected, onChange, rule }: SelectValuePickerProps) => {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const filtered = options.filter((o) =>
    q ? o.label?.toLowerCase().includes(q.toLowerCase()) : true
  );

  return (
    <div className="w-56">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          autoFocus
          type="text"
          className="w-full text-sm bg-transparent outline-none placeholder:text-gray-400"
          placeholder={t("Search...")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="max-h-60 overflow-y-auto py-1">
        {filtered.map((opt) => {
          const isChecked = selected.has(String(opt.value));
          const colorCode = opt.color
            ? colorCodes.find((cc) => cc.color === opt.color)?.code
            : null;
          return (
            <button
              key={opt.value}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${isChecked ? "bg-blue-50" : ""}`}
              onClick={() => {
                let newVal: (string | number)[];
                if (isChecked) {
                  newVal = rule.fieldValue.filter((v) => String(v) !== String(opt.value));
                } else {
                  newVal = [...rule.fieldValue, opt.value];
                }
                onChange(newVal);
              }}
            >
              {colorCode && (
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: colorCode }}
                />
              )}
              <span className="truncate">{opt.label}</span>
              {isChecked && <span className="ml-auto text-blue-600 text-xs">✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

type FieldOption = {
  label: string;
  value: string;
  data: any;
};

type FilterTagProps = {
  fields: FieldOption[];
  rule: FilterRuleProps;
  index: number;
  showLogic?: boolean;
  showAdd?: boolean;
  onChange: (rule: FilterRuleProps) => void;
  onRemove: () => void;
  onAdd?: (rule: FilterRuleProps) => void;
};

/* ────── operator helpers ────── */
const selectConditions = [
  { value: "in", label: "has" },
  { value: "nin", label: "has not" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];
const textConditions = [
  { value: "regex", label: "contains" },
  { value: "ne_regex", label: "does not contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];
const numberConditions = [
  { value: "is", label: "is" },
  { value: "is_not", label: "is not" },
  { value: "lt", label: "less than" },
  { value: "gt", label: "greater than" },
  { value: "lte", label: "less than or equal" },
  { value: "gte", label: "greater than or equal" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];
const dateTimeConditions = [
  { value: "dateRange", label: "between" },
  { value: "before", label: "before" },
  { value: "after", label: "after" },
  { value: "empty", label: "empty" },
  { value: "not_empty", label: "not empty" },
];

/** Operators that don't require a value input */
const noValueOperators = new Set(["empty", "not_empty"]);

/**
 * Inline editable filter tag row.
 * Uses Radix UI Popover for all dropdowns — avoids HeadlessUI nested popover conflicts.
 * Layout: [Where/And] [icon field ▾] [operator ▾] [value ▾] [⋮] [+]
 */
const FilterTag = ({
  fields,
  rule,
  index,
  showLogic = true,
  showAdd = false,
  onChange,
  onRemove,
  onAdd,
}: FilterTagProps) => {
  const { t } = useTranslation();
  const { projectId }: any = useParams();
  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const [accountOptions, setAccountOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [operatorOpen, setOperatorOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const fieldMeta = fields.find((f) => f.value === rule.fieldId);
  const fieldLabel = fieldMeta ? t(fieldMeta.label) : rule.fieldId;
  const fieldData = fieldMeta?.data || {};

  const isDateTime = fieldData.type === "datetime";
  const isNumeric = fieldData.type === "double" || fieldData.type === "percent";
  const valueByFields = ruleReducer.get(rule.fieldId)?.toJS() || [];
  const isSelect =
    valueByFields.length > 0 || rule.fieldId === "account" || rule.fieldId === "contact";

  useEffect(() => {
    if (rule.fieldId === "account") {
      apiSearch({ projectId, pageSize: 50, types: ["COMPANY"], filters: {} }).then((res) => {
        setAccountOptions(
          fromJS(res)
            .getIn(["pageResult", "content"])
            .map((i: any) => ({
              value: i.get("id"),
              label: i.getIn(["data", "name", "value"]) || i.getIn(["data", "email", "value"]),
            }))
            .toJS()
        );
      });
    } else if (rule.fieldId === "contact") {
      apiSearch({ projectId, pageSize: 50, types: ["CONTACT"], filters: {} }).then((res) => {
        setContactOptions(
          fromJS(res)
            .getIn(["pageResult", "content"])
            .map((i: any) => ({
              value: i.get("id"),
              label: i.getIn(["data", "name", "value"]) || i.getIn(["data", "email", "value"]),
            }))
            .toJS()
        );
      });
    }
  }, [rule.fieldId, projectId]);

  const getConditions = () => {
    if (isDateTime) return dateTimeConditions;
    if (isSelect) return selectConditions;
    if (isNumeric) return numberConditions;
    return textConditions;
  };

  const operatorLabel =
    getConditions().find((c) => c.value === rule.operator)?.label || rule.operator;

  const getValueOptions = () => {
    if (rule.fieldId === "account")
      return [{ value: "unknown", label: "No account" }, ...accountOptions];
    if (rule.fieldId === "contact")
      return [{ value: "unknown", label: "No contact" }, ...contactOptions];
    return [{ value: "unknown", label: "(Blanks)" }, ...valueByFields];
  };

  /* Value display text */
  const getValueDisplay = () => {
    if (!rule.fieldValue || rule.fieldValue.length === 0) {
      if (isDateTime) return t("Select date...");
      if (isSelect) return t("Select...");
      return t("Type...");
    }
    if (isDateTime && rule.fieldValue.length === 2) {
      const from = isValid(new Date(rule.fieldValue[0] as string))
        ? new Date(rule.fieldValue[0] as string).toLocaleDateString()
        : "";
      const to = isValid(new Date(rule.fieldValue[1] as string))
        ? new Date(rule.fieldValue[1] as string).toLocaleDateString()
        : "";
      return from && to ? `${from} – ${to}` : from || to || t("Select date...");
    }
    if (isSelect) {
      const opts = getValueOptions();
      const labels = rule.fieldValue
        .map((v) => opts.find((o) => o.value === v)?.label || v)
        .filter(Boolean);
      return labels.length > 0 ? labels.join(", ") : t("Select...");
    }
    return String(rule.fieldValue[0] || "");
  };

  /* ────── Value editor popover content ────── */
  const renderValueEditor = () => {
    if (isDateTime) {
      let dateTime: DateRange | undefined = undefined;
      if (rule.fieldValue && rule.fieldValue.length === 2) {
        const from = isValid(new Date(rule.fieldValue[0] as string))
          ? new Date(rule.fieldValue[0] as string)
          : undefined;
        const to = isValid(new Date(rule.fieldValue[1] as string))
          ? new Date(rule.fieldValue[1] as string)
          : undefined;
        if (from) dateTime = { from, to };
      }
      return (
        <DateRangePickerField
          value={dateTime}
          onChange={(val) => {
            const start = val?.from ? formatISO(val.from) : "";
            const end = val?.to ? formatISO(val.to) : "";
            onChange({ ...rule, fieldValue: [start, end] });
          }}
          classNameContainer="min-w-50"
          className="w-full h-8 rounded-lg px-3 text-sm placeholder:text-gray-400 border border-gray-200 focus:outline-none"
          showClear
        />
      );
    }

    if (isSelect) {
      const options = getValueOptions();
      const selected = new Set(rule.fieldValue.map(String));
      return (
        <SelectValuePicker
          options={options}
          selected={selected}
          rule={rule}
          onChange={(newValues) => onChange({ ...rule, fieldValue: newValues })}
        />
      );
    }

    // Text / number input
    return (
      <div className="p-3 w-56">
        <InputDebounce
          autoFocus
          type={isNumeric ? "number" : "text"}
          className="w-full h-8 rounded-lg px-3 text-sm placeholder:text-gray-400 border border-gray-300 focus:outline-none"
          placeholder={t("Type value...")}
          value={rule.fieldValue.length > 0 ? rule.fieldValue[0] : ""}
          onChange={(val: any) => {
            onChange({ ...rule, fieldValue: val ? [isNumeric ? parseFloat(val) : val] : [] });
          }}
        />
      </div>
    );
  };

  const getMenuStyle = (): React.CSSProperties => {
    if (!menuBtnRef.current) return {};
    const rect = menuBtnRef.current.getBoundingClientRect();
    return {
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.right - 160,
      zIndex: 9999,
    };
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Logic label */}
      {showLogic && (
        <span className="px-2 py-1 text-xs text-gray-500 rounded-md border border-dashed border-gray-300">
          {index === 0 ? t("Where") : t("And")}
        </span>
      )}

      {/* Field segment */}
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-700 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer outline-none">
            <span className="shrink-0">{renderFieldIcon(fieldData)}</span>
            <span className="truncate max-w-24">{fieldLabel}</span>
            <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-auto p-0 bg-white z-[9999]">
          <FieldPicker
            fields={fields}
            onSelect={(newRule) => {
              onChange({
                operator: "",
                fieldType: newRule.fieldType,
                fieldId: newRule.fieldId,
                fieldValue: [],
              });
            }}
            onClose={() => {}}
          />
        </PopoverContent>
      </Popover>

      {/* Operator segment */}
      {rule.fieldId && (
        <Popover open={operatorOpen} onOpenChange={setOperatorOpen}>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer outline-none">
              <span>{operatorLabel || t("Condition")}</span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={4} className="w-auto p-0 bg-white z-[9999]">
            <div className="py-1 min-w-28">
              {getConditions().map((cond) => (
                <button
                  key={cond.value}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    // For no-value operators (empty/not_empty), auto-set a marker value
                    if (noValueOperators.has(cond.value)) {
                      onChange({
                        ...rule,
                        operator: cond.value,
                        fieldValue: ["__" + cond.value + "__"],
                      });
                    } else {
                      onChange({ ...rule, operator: cond.value });
                    }
                    setOperatorOpen(false);
                  }}
                >
                  {t(cond.label)}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Value segment — hidden for no-value operators (empty/not_empty) */}
      {rule.fieldId && rule.operator && !noValueOperators.has(rule.operator) && isDateTime && (
        <div className="inline-flex items-center">{renderValueEditor()}</div>
      )}

      {/* Value segment — non-datetime fields use Radix Popover */}
      {rule.fieldId && rule.operator && !noValueOperators.has(rule.operator) && !isDateTime && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-700 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer outline-none max-w-40">
              <span className="truncate">{getValueDisplay()}</span>
              <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={4} className="w-auto p-0 bg-white z-[9999]">
            {renderValueEditor()}
          </PopoverContent>
        </Popover>
      )}

      {/* ⋮ menu — uses portal to escape parent containers */}
      <button
        ref={menuBtnRef}
        className="ml-auto p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer outline-none"
        onClick={() => setShowMenu(!showMenu)}
      >
        <EllipsisVertical className="w-3.5 h-3.5" />
      </button>
      {showMenu &&
        createPortal(
          <div onMouseDown={(e) => e.stopPropagation()}>
            <div
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onMouseDown={() => setShowMenu(false)}
            />
            <div
              style={getMenuStyle()}
              className="w-40 bg-white rounded-lg border border-gray-200 shadow-lg py-1"
            >
              <button
                className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                onClick={() => {
                  onRemove();
                  setShowMenu(false);
                }}
              >
                {t("Delete condition")}
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* + button — hidden when current filter is incomplete */}
      {showAdd &&
        onAdd &&
        rule.fieldId &&
        rule.operator &&
        rule.fieldValue &&
        rule.fieldValue.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer outline-none">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" sideOffset={4} className="w-auto p-0 bg-white z-[9999]">
              <FieldPicker
                fields={fields}
                onSelect={(newRule) => {
                  onAdd(newRule);
                }}
                onClose={() => {}}
              />
            </PopoverContent>
          </Popover>
        )}
    </div>
  );
};

export default FilterTag;
